"use client"

import Link from "next/link"
import { ArrowRight, Target, Zap, FileText, AlertCircle, Book, Dumbbell } from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { interactiveSurface, focusRing, sectionLabel } from "@/lib/dashboard-ui"

interface Destination {
    title: string
    desc: string
    href: string
    icon: LucideIcon
    /** Shown in Easy mode too. Everything else is Expert-only. */
    essential?: boolean
}

const DESTINATIONS: Destination[] = [
    { title: "Practice", desc: "Drill skills by topic", href: "/dashboard/practice", icon: Target, essential: true },
    { title: "Quiz", desc: "Quick timed rounds", href: "/dashboard/quiz", icon: Zap, essential: true },
    { title: "Mock test", desc: "Full-length, exam timed", href: "/dashboard/mock-test", icon: FileText, essential: true },
    { title: "Mistakes log", desc: "Rework what you got wrong", href: "/dashboard/mistakes", icon: AlertCircle, essential: true },
    { title: "Reading", desc: "Passages and questions", href: "/dashboard/reading", icon: Book },
    { title: "Vocab", desc: "Build your word bank", href: "/dashboard/vocab", icon: Dumbbell },
]

/**
 * The dashboard's reason to exist: somewhere to start studying.
 *
 * Before this, the home page linked to Discord, the LMS, a coupon and a
 * university finder — but to no practice surface at all. Getting to a question
 * meant going through the sidebar.
 *
 * "Practice" is the single primary action (filled); the rest are peers.
 */
export function QuickActions({ isEasyMode = false }: { isEasyMode?: boolean }) {
    const items = isEasyMode ? DESTINATIONS.filter((d) => d.essential) : DESTINATIONS
    const [primary, ...rest] = items

    return (
        <section aria-labelledby="quick-actions-heading">
            <h2 id="quick-actions-heading" className={cn(sectionLabel, "mb-3")}>
                Start studying
            </h2>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Link
                    href={primary.href}
                    className={cn(
                        "group relative flex items-center gap-4 overflow-hidden rounded-2xl p-5",
                        "bg-primary text-primary-foreground",
                        "transition-[box-shadow,transform] duration-200 ease-out",
                        "motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-lg motion-safe:hover:shadow-primary/25",
                        focusRing
                    )}
                >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-foreground/15">
                        <primary.icon className="h-6 w-6" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                        <span className="block text-base font-bold leading-tight">{primary.title}</span>
                        <span className="mt-0.5 block text-sm text-primary-foreground/90">{primary.desc}</span>
                    </span>
                    <ArrowRight
                        className="h-5 w-5 shrink-0 motion-safe:transition-transform motion-safe:duration-200 motion-safe:group-hover:translate-x-1"
                        aria-hidden="true"
                    />
                </Link>

                {rest.map(({ title, desc, href, icon: Icon }) => (
                    <Link key={href} href={href} className={cn(interactiveSurface, "group flex items-center gap-4 p-5")}>
                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-muted text-foreground transition-colors duration-200 group-hover:bg-primary/10 group-hover:text-primary">
                            <Icon className="h-6 w-6" aria-hidden="true" />
                        </span>
                        <span className="min-w-0 flex-1">
                            <span className="block text-base font-bold leading-tight text-foreground">{title}</span>
                            <span className="mt-0.5 block text-sm text-muted-foreground">{desc}</span>
                        </span>
                        <ArrowRight
                            className="h-5 w-5 shrink-0 text-muted-foreground motion-safe:transition-transform motion-safe:duration-200 motion-safe:group-hover:translate-x-1"
                            aria-hidden="true"
                        />
                    </Link>
                ))}
            </div>
        </section>
    )
}
